import { useState } from 'preact/hooks'
import type { MountOptions } from './mount'
import type { DetectedInvoice } from './types'
import Dropzone from './components/Dropzone'
import ReviewView, { type SubmitData, type SubmitMode } from './components/ReviewView'
import SuccessView, { type SentInfo } from './components/SuccessView'
import RateLimitView from './components/RateLimitView'
import { sendInvoices, captureLead } from './lib/api'
import { extractPagesAsPdf, downloadBlob, zipFiles } from './lib/pdf'

const SESSION_KEY = 'spendifique-tools:lead-email'

type Props = { options: MountOptions }

export default function App({ options }: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [invoices, setInvoices] = useState<DetectedInvoice[]>([])
  const [sent, setSent] = useState<SentInfo | null>(null)
  const [submittingMode, setSubmittingMode] = useState<SubmitMode | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [rateLimited, setRateLimited] = useState(false)

  const reset = () => {
    setFile(null)
    setInvoices([])
    setSent(null)
    setSubmitError(null)
    setRateLimited(false)
  }

  const initialEmail = (() => {
    try {
      return localStorage.getItem(SESSION_KEY)
    } catch {
      return null
    }
  })()

  const ensureExt = (name: string) =>
    name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`

  const pagesToRange = (pages: number[]): string => {
    if (pages.length === 0) return ''
    if (pages.length === 1) return String(pages[0])
    return `${pages[0]}-${pages[pages.length - 1]}`
  }

  /**
   * Splits the PDF for each selection client-side, then bundles the results
   * into a single zip download. Single-selection downloads skip the zip and
   * deliver the PDF directly so users don't have to unpack a one-file zip.
   */
  const runClientDownloads = async (
    selections: { id: string; filename: string }[],
    list: DetectedInvoice[],
    src: File,
  ) => {
    const files: { filename: string; blob: Blob }[] = []
    for (const { id, filename } of selections) {
      const inv = list.find((x) => x.id === id)
      if (!inv) continue
      const blob = await extractPagesAsPdf(src, inv.pages)
      files.push({ filename: ensureExt(filename), blob })
    }
    if (files.length === 0) return

    if (files.length === 1) {
      downloadBlob(files[0].blob, files[0].filename)
      return
    }

    const baseName = src.name.toLowerCase().endsWith('.pdf')
      ? src.name.slice(0, -4)
      : src.name
    const zipBlob = await zipFiles(files)
    downloadBlob(zipBlob, `${baseName || 'invoices'}-split.zip`)
  }

  const handleSubmit = async (data: SubmitData) => {
    if (!file) return
    setSubmittingMode(data.mode)
    setSubmitError(null)

    try {
      // Persist filename edits back to invoices in case the user retries.
      const updated = invoices.map((inv) => {
        const sel = data.selections.find((s) => s.id === inv.id)
        return sel ? { ...inv, suggestedFilename: sel.filename } : inv
      })
      setInvoices(updated)

      if (data.mode === 'download') {
        if (data.consented && data.email) {
          captureLead(
            {
              email: data.email,
              consented: data.consented,
              termsAccepted: data.termsAccepted,
              source: 'invoice_splitter',
              metadata: {
                originalFilename: file.name,
                selectedCount: data.selections.length,
              },
            },
            options.apiBase,
          )
            .then(() => {
              try {
                localStorage.setItem(SESSION_KEY, data.email)
              } catch {
                // ignore
              }
            })
            .catch((e) => {
              console.warn('[SpendifiqueInvoiceSplitter] lead capture failed', e)
            })
        }

        await runClientDownloads(data.selections, updated, file)
        setSent({ mode: 'download', invoiceCount: data.selections.length })
        return
      }

      // mode === 'email'
      const selections = data.selections
        .map((sel) => {
          const inv = updated.find((i) => i.id === sel.id)
          if (!inv) return null
          return { filename: sel.filename, pageRange: pagesToRange(inv.pages) }
        })
        .filter((s): s is { filename: string; pageRange: string } => s !== null)

      const result = await sendInvoices(
        file,
        {
          email: data.email,
          consented: data.consented,
          termsAccepted: data.termsAccepted,
          source: 'invoice_splitter',
          originalFilename: file.name,
          selections,
        },
        options.apiBase,
      )

      try {
        localStorage.setItem(SESSION_KEY, data.email)
      } catch {
        // ignore — private mode
      }

      setSent({
        mode: 'email',
        email: result.sentTo,
        invoiceCount: result.invoiceCount,
      })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error'
      console.error('[SpendifiqueInvoiceSplitter] submit failed', e)
      setSubmitError(msg)
    } finally {
      setSubmittingMode(null)
    }
  }

  if (rateLimited) {
    return (
      <div className="sis-root">
        <RateLimitView onReset={reset} />
      </div>
    )
  }

  if (sent) {
    return (
      <div className="sis-root">
        <SuccessView info={sent} onReset={reset} />
      </div>
    )
  }

  return (
    <div className="sis-root">
      {file && invoices.length > 0 ? (
        <ReviewView
          file={file}
          invoices={invoices}
          initialEmail={initialEmail}
          onSubmit={handleSubmit}
          onReset={reset}
          submittingMode={submittingMode}
          errorMessage={submitError}
        />
      ) : (
        <Dropzone
          options={options}
          onAnalyzed={(f, list) => {
            setFile(f)
            setInvoices(list)
          }}
          onRateLimited={() => setRateLimited(true)}
        />
      )}
    </div>
  )
}
